import { Chip, ListItem } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Button, Modal, Spinner, Table } from 'reactstrap';

import Icon from '@/components/icon';
import Placeholder from '@/config/placeholder.config';
import { Constants } from '@/constants';
import Cigar from '@/models/Cigar';
import Vitola from '@/models/Vitola';

const CigarMerger = ({
  open,
  onClose,
  onCancel,
  sourceCigar,
  onMerged = () => {},
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [query, setQuery] = useState('');
  const [cigars, setCigars] = useState([]);
  const [targetCigar, setTargetCigar] = useState({});
  const [mergePlan, setMergePlan] = useState({});
  const [merging, setMerging] = useState(false);
  const [loading, setLoading] = useState(false);

  const mergedCigar = useMemo(() => {
    const m = { ...targetCigar };
    mergePlan.fieldChanges?.forEach(({ field, to }) => {
      m[field] = to;
    });
    return m;
  }, [mergePlan, targetCigar]);

  const getMergePreview = () => {
    if (targetCigar) {
      setLoading(true);
      axios
        .post(
          `${Constants.apiPath}/cigars/${targetCigar.id}/merge/${sourceCigar.id}/preview`
        )
        .then((res) => {
          console.log(res.data);
          setMergePlan(res.data);
          setShowConfirmModal(true);
          if (typeof onClose === 'function') {
            onClose();
          }
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // TODO Error if no mergeCigar selected
    }
  };

  const merge = () => {
    if (targetCigar) {
      setMerging(true);
      axios
        .post(
          `${Constants.apiPath}/cigars/${targetCigar.id}/merge/${sourceCigar.id}`
        )
        .then((res) => {
          console.log(res.data);
          setShowConfirmModal(false);
          if (typeof onMerged === 'function') {
            onMerged(res.data);
          }
        })
        .catch((e) => {
          console.log(e);
          // TODO Error alert
        })
        .finally(() => {
          setMerging(false);
        });
    } else {
      // TODO Error if no mergeCigar selected
    }
  };

  const loadCigars = useCallback(() => {
    const cigarUrl = `${Constants.apiPath}/cigars/datatables/all`;
    console.log(cigarUrl);
    axios
      .get(cigarUrl, {
        params: {
          q: query,
        },
      })
      .then((res) => {
        setCigars(res.data.data.filter((c) => c.id !== sourceCigar.id));
      })
      .catch((e) => {
        console.log(e);
      });
  }, [query]);

  const renderMergeModal = () => (
    <Modal isOpen={open} onClosed={onClose} toggle={onClose}>
      <div
        className="modal-header"
        style={{ flexDirection: 'column', alignItems: 'flex-start' }}
      >
        <h5 className="modal-title">{'Merge Cigar'}</h5>
        <div style={{ marginTop: 12, width: '100%' }}>
          Select cigar to merge into:
          <input
            className="form-control sticky"
            placeholder="Search cigars..."
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              // FIXME Debounce this
              loadCigars();
            }}
          />
        </div>
      </div>
      <div
        className="modal-body"
        style={{ maxHeight: isMobile ? '80vh' : 500, overflowY: 'auto' }}
      >
        <List>
          {query.length > 0 &&
            cigars.length > 0 &&
            cigars.map((cigar) => (
              <ListItem
                key={`cigar-item-${cigar.id}`}
                onClick={() =>
                  setTargetCigar({
                    ...cigar,
                    full_name: cigar.full_name.replace(' null', '').trim(),
                  })
                }
              >
                <Badge
                  color="secondary"
                  overlap="circle"
                  badgeContent={
                    <Icon
                      name="check"
                      style={{ margin: 0, height: 8, width: 8 }}
                    />
                  }
                  invisible={targetCigar.id !== cigar.id}
                >
                  <Avatar
                    variant="square"
                    src={Cigar.getBandImage(cigar)}
                    style={{ marginRight: 10, height: 80, width: 80 }}
                    alt={cigar.full_name}
                    imgProps={{ style: { objectFit: 'contain' } }}
                  >
                    <img
                      src={Placeholder.cigar}
                      style={{ height: 80, width: 80 }}
                    />
                  </Avatar>
                </Badge>
                <ListItemText
                  primary={
                    <span
                      className="padded-multiline"
                      style={{ marginLeft: 10 }}
                    >
                      {cigar.full_name.replace(' null', '').trim()}
                    </span>
                  }
                />
              </ListItem>
            ))}
          {cigars.length === 0 && (
            <div style={{ textAlign: 'center', margin: 10 }}>
              No cigars found
            </div>
          )}
        </List>
      </div>
      <div className="modal-footer">
        <Button color="light" onClick={onCancel}>
          {'Cancel'}
        </Button>
        <Button
          onClick={getMergePreview}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading && (
            <Spinner color="dark" style={{ marginRight: 8 }} size="sm" />
          )}
          {loading ? 'Getting data...' : 'Continue'}
        </Button>
      </div>
    </Modal>
  );

  const formatCell = (cellName: string, cellValue: any) => {
    if (cellName === 'vitolas' && cellValue) {
      return (
        <div>
          {cellValue.map((vitola) => (
            <div key={vitola.id}>{Vitola.formattedName(vitola)}</div>
          ))}
        </div>
      );
    }
    if (cellName === 'tags' && cellValue) {
      return (
        <div>
          {cellValue.map((cigarTag) => (
            <Chip
              key={cigarTag.id}
              label={cigarTag.title || cigarTag.tag?.title}
              color="default"
              size="small"
              style={{ margin: 4 }}
            />
          ))}
        </div>
      );
    }
    if (cellName === 'images' && cellValue) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {cellValue.map((image, index) => (
            <div style={{ marginLeft: index === 0 ? 0 : 8 }} key={image.id}>
              <img
                src={image.image_url}
                style={{ height: 50, width: 50, objectFit: 'contain' }}
                alt={image.image_url}
              />
            </div>
          ))}
        </div>
      );
    }
    return String(cellValue);
  };

  const renderMergeConfirmationModal = () => {
    // TODO Show a side by side comparison of the cigar to be deleted (light red background) and the one after merge
    //  Include a Collapse with "X vitolas v" that expands the list of vitolas on click
    //  also show the number of records affected (sessions, etc)
    return (
      <Modal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
        onClosed={() => setShowConfirmModal(false)}
        style={{ maxWidth: 800 }}
      >
        <div className="modal-header">
          <h5 className="modal-title h2">Confirm</h5>
        </div>
        <div className="modal-body">
          <p>Please confirm the cigar details after the merge:</p>
          <Table bordered>
            <thead>
              <tr>
                <th>Field</th>
                <th>Current Value</th>
                <th>After Merge</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(mergedCigar).map((field) => {
                const change = mergePlan.fieldChanges?.find(
                  (c) => c.field === field
                );
                const isChanged = Boolean(change);
                return (
                  <tr key={field} className={isChanged ? 'table-success' : ''}>
                    <td style={{ textTransform: 'capitalize' }}>
                      {field.replace(/_/g, ' ')}
                    </td>
                    <td>
                      {formatCell(
                        field,
                        change ? change.from : targetCigar[field]
                      )}
                    </td>
                    <td>{formatCell(field, mergedCigar[field])}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <h4>Related Records to Reassign</h4>
          <Table bordered>
            <thead>
              <tr>
                <th>Association</th>
                <th># of Records Affected</th>
              </tr>
            </thead>
            <tbody>
              {mergePlan.associations?.map(({ table, count }) => (
                <tr key={table}>
                  <td>{table}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="modal-footer">
          <Button
            onClick={() => {
              setShowConfirmModal(false);
              if (typeof onCancel === 'function') {
                onCancel();
              }
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={merging}
            onClick={merge}
            variant="contained"
            color="danger"
          >
            {merging && (
              <Spinner
                type="border"
                color="dark"
                size="sm"
                style={{ marginRight: 10 }}
              />
            )}
            {merging ? 'Merging...' : 'Confirm'}
          </Button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {renderMergeModal()}
      {renderMergeConfirmationModal()}
    </>
  );
};

export default CigarMerger;
